// src/utils/pushNavigation.ts
/**
 * Переход в чат из push-уведомления.
 *
 * Когда приложение уже открыто, service worker не открывает новое окно, а
 * присылает в него `postMessage({ type: 'OPEN_CHAT', conversationId })`.
 * Новый роутинг для этого не нужен: сообщение превращается в уже существующее
 * событие `OPEN_CHAT_EVENT` (`utils/messageAlerts.ts`), на которое `App.tsx`
 * навигирует в `/dashboard/chat/<conversationId>`.
 *
 * Если окно было закрыто, service worker сам открывает нужный маршрут через
 * `clients.openWindow` — там этот код уже не участвует.
 */
import { openChat } from './messageAlerts';

/** Тип сообщения, который присылает service worker (см. `service-worker.ts`). */
export const PUSH_OPEN_CHAT_MESSAGE = 'OPEN_CHAT';

const readConversationId = (data: unknown): string | null => {
    if (typeof data !== 'object' || data === null) return null;
    const value = (data as { conversationId?: unknown }).conversationId;
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
};

/**
 * Подписаться на сообщения от service worker'а.
 * Возвращает функцию отписки (пригодится в тестах).
 */
export const installPushNavigation = (): (() => void) => {
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
        return () => undefined;
    }

    const handleMessage = (event: MessageEvent): void => {
        const data: unknown = event && event.data;
        if (typeof data !== 'object' || data === null) return;
        if ((data as { type?: unknown }).type !== PUSH_OPEN_CHAT_MESSAGE) return;

        const conversationId = readConversationId(data);
        if (!conversationId) return;

        // `openChat` диспатчит OPEN_CHAT_EVENT — дальше навигацию делает App.
        openChat(conversationId);
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
};
